import type {
  CacheEntry as Entry,
  HasManyDesc,
  ModelCache,
  ModelDesc,
  ModelDescriptions,
  ModelName,
} from "./mate-types";
import {INVALIDATE} from "./mate-shared";
import {filterFields, parseRelationKey, type RelationKey} from "./relation-filter";

/** What the cache doesn't know, as opposed to a known `null`/`undefined`. */
export const ABSENT = Symbol("ABSENT");

/** `$order` of a relation key, as fields to compare with a direction. */
export type OrderSpec = {field: string; desc: boolean}[];

/** What list upkeep needs to know about a relation key; parsed once per key string. */
export type KeyInfo = RelationKey & {
  key: string;
  /** the query without `$limit`, `$first`, `$offset`, which don't decide whether a row matches */
  matchQuery: Record<string, unknown> | null;
  /** only the first rows of the matches are cached */
  isWindow: boolean;
  /** the rows before `$offset` aren't cached, so a row leaving them shifts the window */
  hasOffset: boolean;
  limit: number | null;
  order: OrderSpec | null;
};

const keyInfoMemo = new Map<string, KeyInfo | null>();

const getKeyInfo = (key: string): KeyInfo | null => {
  const existing = keyInfoMemo.get(key);
  if (existing !== undefined) return existing;
  const parsed = parseRelationKey(key);
  const getInfo = (): KeyInfo | null => {
    if (!parsed) return null;
    const {query} = parsed;
    if (!query)
      return {
        ...parsed,
        key,
        matchQuery: null,
        isWindow: false,
        hasOffset: false,
        limit: null,
        order: null,
      };
    const {$limit, $first, $offset, ...matchQuery} = query;
    const rawOrder = query.$order as string | string[] | undefined;
    const getLimit = () => {
      if ($first) return 1;
      return typeof $limit === "number" ? $limit : null;
    };
    return {
      ...parsed,
      key,
      matchQuery,
      isWindow: $limit !== undefined || $first !== undefined || $offset !== undefined,
      hasOffset: $offset !== undefined,
      limit: $offset === undefined ? getLimit() : null,
      order: rawOrder
        ? (Array.isArray(rawOrder) ? rawOrder : [rawOrder]).map((f) =>
            f[0] === "-" ? {field: f.slice(1), desc: true} : {field: f, desc: false}
          )
        : null,
    };
  };
  const info = getInfo();
  keyInfoMemo.set(key, info);
  return info;
};

export type ParentRef = {model: ModelName; relName: string; fk: string | null};

/** A field's name as the row holds it: a parent under the relation's name, not its fk. */
export const fieldName = (desc: ModelDesc, field: string) =>
  desc.fkToBelongsTo[field]?.name ?? field;

/**
 * Writes one field onto a row, keeping a parent's fk and relation name in step. `INVALIDATE` makes
 * the field unknown.
 */
export const setRowField = (
  desc: ModelDesc,
  row: Record<string, unknown>,
  field: string,
  val: unknown
) => {
  const name = fieldName(desc, field);
  const fk = desc.belongsTo[name]?.fk;
  if (val === INVALIDATE) {
    delete row[name];
    if (fk) delete row[fk];
  } else {
    row[name] = val;
    if (fk) row[fk] = val;
  }
};

/** Relation keys per parent model, e.g. {account: Set(['cards({"title":"x"})'])}. */
export type KeysByParent = Partial<Record<ModelName, Set<string>>>;

/** {card: {title: {account: Set(['cards({"title":"x"})'])}}} */
export type FieldsAffectingRelations = Partial<Record<ModelName, Record<string, KeysByParent>>>;

export type TargetingContext = {
  descriptions: ModelDescriptions;
  /** the relations holding each model's rows: {attachment: [{model: "card", relName: "attachments", fk: "cardId"}]} */
  hasManyModelDeps: Record<ModelName, ParentRef[]>;
  /** the fields a relation key's query reads on the relation's own rows */
  fieldsAffectingRelations: FieldsAffectingRelations;
};

/**
 * The cached lists of each parent, read through `read`: the loaded cache, or the optimistic view on
 * top of it. Memos assume the loaded cache doesn't change while one lives.
 */
export class CachedLists {
  private keysByParentEntry = new Map<Entry, Map<string, KeyInfo[]>>();
  private parentIds = new Map<ModelName, string[]>();
  /** per `parentModel:relName`: row id -> parent id -> number of the parent's lists holding it */
  private holderIndexes = new Map<string, Map<string, Map<string, number>>>();

  constructor(
    private readonly modelCache: ModelCache,
    private readonly read: (model: ModelName, id: string, key: string) => unknown,
    /** told of each loaded row read, or of every row of a model with a null id */
    private readonly onRead?: (model: ModelName, id: string | null) => void
  ) {}

  private loadedEntry(model: ModelName, id: string): Entry | null | undefined {
    this.onRead?.(model, id);
    if (model === "_root") return this.modelCache._root as unknown as Entry | undefined;
    return this.modelCache[model]?.[id];
  }

  /** Whether the loaded cache holds the row. */
  isLoaded(model: ModelName, id: string | null) {
    return id !== null && Boolean(this.loadedEntry(model, id));
  }

  /** The keys a parent's loaded entry holds for the relation: its lists, counts and queries. */
  keysOf(parent: ParentRef, parentId: string): KeyInfo[] {
    const entry = this.loadedEntry(parent.model, parentId);
    if (!entry) return [];
    let byRelName = this.keysByParentEntry.get(entry);
    if (!byRelName) {
      byRelName = new Map();
      for (const key of Object.keys(entry.value)) {
        const info = getKeyInfo(key);
        if (!info) continue;
        const list = byRelName.get(info.relName);
        if (list) list.push(info);
        else byRelName.set(info.relName, [info]);
      }
      this.keysByParentEntry.set(entry, byRelName);
    }
    return byRelName.get(parent.relName) ?? [];
  }

  parentIdsOf(parent: ParentRef): readonly string[] {
    if (parent.model === "_root") return ["_root"];
    this.onRead?.(parent.model, null);
    let ids = this.parentIds.get(parent.model);
    if (!ids) {
      const entries = this.modelCache[parent.model];
      ids = entries ? Object.keys(entries).filter((id) => entries[id]) : [];
      this.parentIds.set(parent.model, ids);
    }
    return ids;
  }

  /** Which parents' lists of this relation hold a row; the index is built once per relation. */
  holders(parent: ParentRef, rowId: string): ReadonlyMap<string, number> | undefined {
    return this.holderIndex(parent).get(rowId);
  }

  private holderIndex(parent: ParentRef) {
    const indexKey = `${parent.model}:${parent.relName}`;
    let index = this.holderIndexes.get(indexKey);
    if (index) return index;
    index = new Map();
    this.holderIndexes.set(indexKey, index);
    for (const parentId of this.parentIdsOf(parent)) {
      for (const info of this.keysOf(parent, parentId)) {
        if (info.variant !== "list") continue;
        const val = this.read(parent.model, parentId, info.key);
        const ids = Array.isArray(val) ? val : typeof val === "string" ? [val] : [];
        for (const id of ids) bump(index, id, parentId, 1);
      }
    }
    return index;
  }

  /** Keeps a built index in step with an edit of a list; false when there's none to keep. */
  noteMembership(parent: ParentRef, rowId: string, parentId: string, delta: 1 | -1) {
    const index = this.holderIndexes.get(`${parent.model}:${parent.relName}`);
    if (!index) return false;
    bump(index, rowId, parentId, delta);
    return true;
  }
}

const bump = (
  index: Map<string, Map<string, number>>,
  rowId: string,
  parentId: string,
  delta: 1 | -1
) => {
  let parents = index.get(rowId);
  if (!parents) {
    parents = new Map();
    index.set(rowId, parents);
  }
  const count = (parents.get(parentId) ?? 0) + delta;
  if (count > 0) parents.set(parentId, count);
  else parents.delete(parentId);
};

/**
 * How a parent was found:
 * - `fk`: the row's fk names it
 * - `held`: one of its cached lists held the row before the write
 * - `hint`: the row's fk names it, but the relation is a list the parent keeps (`fkAsArray`), which
 *   the fk only hints at
 */
type FoundBy = "fk" | "held" | "hint";

/** The cached lists of one relation a row write can change. */
export type ListTargets = {
  parent: ParentRef;
  relDesc: HasManyDesc;
  /** the row's fk tells which parent holds it */
  isPlainFk: boolean;
  /** only these keys of each parent, or null for every key of the relation */
  onlyKeys: ReadonlySet<string> | null;
  parents: ReadonlyMap<string, FoundBy>;
  /** the parents couldn't all be told: any parent's lists may change */
  anyParent: boolean;
  /**
   * for `anyParent`: a deleted row no cached list holds only changes the counts and windows past an
   * `$offset` of the other parents
   */
  anyParentCountsOnly: boolean;
};

const foundRank: Record<FoundBy, number> = {hint: 0, fk: 1, held: 2};

/** The row's parent id, whether the row holds it under the fk or the relation's name. */
export const rowFk = (
  ctx: Pick<TargetingContext, "descriptions">,
  row: Record<string, unknown> | null,
  model: ModelName,
  fk: string
) => {
  if (!row) return ABSENT;
  if (fk in row) return row[fk];
  const relName = ctx.descriptions[model].fkToBelongsTo[fk]?.name;
  return relName && relName in row ? row[relName] : ABSENT;
};

/** What `affectedLists` needs of one relation holding a model's rows; it doesn't change. */
type RelPlan = {
  parent: ParentRef;
  relDesc: HasManyDesc;
  /** the row's fk tells which parent holds it */
  isPlainFk: boolean;
  /** the row's fk that names the parent, if any */
  fk: string | null;
  fkRelName: string | undefined;
  /** the fields the relation's `filter` reads */
  filterFields: string[];
  /** relations of the same parent on the same fk: a list of any of them holding the row names its parent */
  sameFkParents: ParentRef[];
};

const relPlanMemo = new WeakMap<TargetingContext["hasManyModelDeps"], Map<ModelName, RelPlan[]>>();

const relPlansOf = (ctx: TargetingContext, model: ModelName): RelPlan[] => {
  let byModel = relPlanMemo.get(ctx.hasManyModelDeps);
  if (!byModel) relPlanMemo.set(ctx.hasManyModelDeps, (byModel = new Map()));
  const existing = byModel.get(model);
  if (existing) return existing;
  const {descriptions} = ctx;
  const modelDesc = descriptions[model];
  const plans = ctx.hasManyModelDeps[model].flatMap((parent): RelPlan[] => {
    const relDesc = descriptions[parent.model].hasMany[parent.relName] as HasManyDesc | undefined;
    if (!relDesc) return [];
    const isPlainFk = !relDesc.fkAsArray && parent.fk !== null;
    const getFk = () => {
      if (isPlainFk) return parent.fk;
      // the parent keeps an fkAsArray list itself; a row's fk to that parent says which one, mostly
      const isHint =
        relDesc.fkAsArray &&
        parent.fk !== null &&
        modelDesc.fkToBelongsTo[parent.fk]?.model === parent.model;
      return isHint ? parent.fk : null;
    };
    const fk = getFk();
    return [
      {
        parent,
        relDesc,
        isPlainFk,
        fk,
        fkRelName: fk ? modelDesc.fkToBelongsTo[fk]?.name : undefined,
        filterFields: relDesc.filter ? filterFields(relDesc.filter, modelDesc) : [],
        sameFkParents: ctx.hasManyModelDeps[model].filter(
          (other) => other.model === parent.model && other.fk === parent.fk
        ),
      },
    ];
  });
  byModel.set(model, plans);
  return plans;
};

/** Keys whose query reads a written field of the relation's own rows. */
const keysReadingWritten = (
  ctx: TargetingContext,
  model: ModelName,
  written: ReadonlySet<string>,
  parentModel: ModelName
) => {
  const keys = new Set<string>();
  const byField = ctx.fieldsAffectingRelations[model];
  if (!byField) return keys;
  const {belongsTo} = ctx.descriptions[model];
  for (const field of written) {
    // a query names a parent by its fk, a write by the relation's name
    for (const name of [field, belongsTo[field]?.fk]) {
      const found = name ? byField[name]?.[parentModel] : undefined;
      if (found) for (const key of found) keys.add(key);
    }
  }
  return keys;
};

/**
 * Every cached list of `model`'s parents that a write of one row can change: the relation, which
 * parents, which of their keys. `before` is null for a created row, `after` null for a deleted one;
 * both hold what's known of the row, a field left out is unknown. `written` names fields as the row
 * does, a parent by the relation's name. `isStale` tells a field of `before` the cache has marked for
 * reloading: its parent may have changed on the server since.
 *
 * The optimistic view edits the lists of parents found by `fk` or `held`; invalidation reloads every
 * list returned.
 */
export const affectedLists = (
  ctx: TargetingContext,
  lists: CachedLists,
  model: ModelName,
  id: string | null,
  written: ReadonlySet<string>,
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
  isStale?: (field: string) => boolean
): ListTargets[] => {
  const isUpdate = before !== null && after !== null;
  const result: ListTargets[] = [];
  for (const plan of relPlansOf(ctx, model)) {
    const {parent, relDesc, isPlainFk, fk, fkRelName} = plan;
    const fkWritten = fk !== null && (written.has(fk) || (fkRelName && written.has(fkRelName)));
    const filterWritten = plan.filterFields.some((f) => written.has(f));

    // an update only reaches lists whose query reads a written field
    let onlyKeys: Set<string> | null = null;
    if (isUpdate && !fkWritten && !filterWritten) {
      onlyKeys = keysReadingWritten(ctx, model, written, parent.model);
      if (!onlyKeys.size) continue;
    }

    const parents = new Map<string, FoundBy>();
    const addParent = (parentId: unknown, foundBy: FoundBy) => {
      if (typeof parentId !== "string") return;
      const prev = parents.get(parentId);
      if (!prev || foundRank[prev] < foundRank[foundBy]) parents.set(parentId, foundBy);
    };
    let anyParent = false;
    let anyParentCountsOnly = false;
    if (parent.model === "_root") {
      parents.set("_root", "fk");
    } else {
      const byFk: FoundBy = isPlainFk ? "fk" : "hint";
      const beforeFk = fk ? rowFk(ctx, before, model, fk) : ABSENT;
      const afterFk = fk ? rowFk(ctx, after, model, fk) : ABSENT;
      addParent(beforeFk, byFk);
      addParent(afterFk, byFk);
      const beforeStale =
        beforeFk !== ABSENT &&
        isStale !== undefined &&
        (isStale(fk!) || (fkRelName !== undefined && isStale(fkRelName)));
      const beforeUnknown = beforeFk === ABSENT || beforeStale;
      // an fkAsArray list is the parent's own, the row's fk only hints at which parent keeps it
      const isDelete = before !== null && after === null;
      if (before !== null && (beforeUnknown || (!isPlainFk && isDelete))) {
        // an update doesn't take a row out of an fkAsArray list, so there only a delete asks
        const getHolders = () => {
          if (id === null) return [];
          if (!isPlainFk) return after ? [] : [...(lists.holders(parent, id)?.keys() ?? [])];
          // with a plain fk, a list of any relation on that fk holding the row names its parent
          return plan.sameFkParents.flatMap((other) => [
            ...(lists.holders(other, id)?.keys() ?? []),
          ]);
        };
        const held = getHolders();
        for (const parentId of held) addParent(parentId, "held");
        // A row the cache doesn't hold and no cached list holds left none of them by moving away.
        // One the cache holds without its parent, or one staying with its unknown parent, may be
        // in any parent's lists
        const couldBeAnywhere = !fk || !fkWritten || lists.isLoaded(model, id);
        if (beforeUnknown && !held.length && couldBeAnywhere) {
          anyParent = true;
          // no cached list holding a deleted row, none of the lists loses it
          if (isDelete && id !== null) anyParentCountsOnly = true;
        }
      }
      // a row going to a parent it doesn't name may enter any parent's lists
      if (after !== null && afterFk === ABSENT && (before === null || fkWritten)) {
        anyParent = true;
        anyParentCountsOnly = false;
      }
    }
    if (!parents.size && !anyParent) continue;
    result.push({parent, relDesc, isPlainFk, onlyKeys, parents, anyParent, anyParentCountsOnly});
  }
  return result;
};

/** The keys of one parent a target reaches; `countsOnly` for a parent only `anyParent` reaches. */
export const targetKeys = (
  lists: CachedLists,
  target: ListTargets,
  parentId: string,
  countsOnly = false
) => {
  const keys = lists.keysOf(target.parent, parentId);
  const {onlyKeys} = target;
  if (!onlyKeys && !countsOnly) return keys;
  return keys.filter(
    (info) =>
      (!onlyKeys || onlyKeys.has(info.key)) &&
      (!countsOnly || info.variant !== "list" || info.hasOffset)
  );
};
